
import React, { useState, useEffect } from 'react';
import { ProCard } from "@ant-design/pro-components";
import { Table, Button, Input, Form, message, Breadcrumb, Modal} from 'antd';
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { TopBreadcrumb } from "@/layouts/TopBreadcrumb";
import { CloseOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { getIPWhiteList, updateIPWhiteList } from "@/apis/instance";

export const ChangeIPWhitelist = ({id, goto}) => {
    const navigate = useResourceNavigate();
    const [dataSource, setDataSource] = useState([]);
    const [count, setCount] = useState(0);
    const [isChanged, setIsChanged] = useState(false);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalForm] = Form.useForm();
    const [isModalOkDisabled, setIsModalOkDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const getIPList = async () => {
          try {
            setIsLoading(true);
            const response = await getIPWhiteList(id);
            setIsLoading(false);
            let list = response.whitelist || [];
            let i = 1;
            setDataSource(list.map((item) => (item.key = i++)&&item));  
            setCount(i);     
          } catch (error) {
            setIsLoading(false);
            setDataSource([]);
            console.error("get built-in users failed:", error);
          }
        };
    
        getIPList();
      }, []);
    const handleAdd = () => {
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        modalForm.validateFields().then(values => {
            if (!isIPValid(values.ip, 'ip')) {
                message.error('IP is invalid.');
                return;
            }
            const newData = {
                key: count,
                ip: values.ip,
                note: values.note,
            };
            setDataSource([...dataSource, newData]);
            setCount(count + 1);
            setIsChanged(true);
            setIsModalVisible(false);
            modalForm.resetFields();
            setIsModalOkDisabled(true);
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        modalForm.resetFields();
        setIsModalOkDisabled(true);
    };  

    const values = Form.useWatch([], modalForm);
    useEffect(() => {
        modalForm
        .validateFields({
            validateOnly: true,
        })
        .then(() => {
            setIsModalOkDisabled(false);
        })
        .catch(() => {
            setIsModalOkDisabled(true);
        });
    }, [modalForm, values]);

    const handleDelete = (key) => {
        const newData = dataSource.filter((item) => item.key !== key);
        setDataSource(newData);
        setIsChanged(true);
    };

    const handleSave = (row) => {
        const newData = [...dataSource];
        const index = newData.findIndex((item) => row.key === item.key);
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });
        setDataSource(newData);
        setIsChanged(true);
    };

    const handleCancel = () => {
        navigate({ resource: goto, action: "list" });
    };

    const doSave = async () => {
        try {
            const response = await updateIPWhiteList(id, dataSource.map((x) => ({"ip":x.ip, "note":x.note})));        
          } catch (error) {
            console.error("Failed to update ip whitelist:", error);
          }
          handleCancel();
    };

    const isIPValid = (value, dataIndex) => {
        const cidrRegex = /^([0-9]{1,3}\.){3}[0-9]{1,3}\/([0-9]|[1-2][0-9]|3[0-2])$/;
        return cidrRegex.test(value);         
    };

    const columns = [
        {
            title: 'Public IP',
            dataIndex: 'ip',
            editable: true,
            width:200
        },
        {
            title: 'Note',
            dataIndex: 'note',
            editable: true,
        },
        {
            title: '',
            width: 50,
            dataIndex: 'action',
            render: (_, record) =>
                dataSource.length >= 1 ? (
                    <Button icon={<MinusOutlined/>} onClick={() => handleDelete(record.key)} shape="circle"/>
                ) : null,
        },
    ];

    const EditableCell = ({
        title,
        editable,
        children,
        dataIndex,
        record,
        handleSave,
        ...restProps
    }) => {
        const [editing, setEditing] = useState(false);
        const inputRef = React.useRef(null);
        useEffect(() => {
            if (editing) {
              inputRef.current?.focus();
            }
          }, [editing]);
        const toggleEdit = () => {
            setEditing(!editing);
            form.setFieldsValue({ [dataIndex]: record[dataIndex] });
        };

        const save = async () => {
            try {
                const values = await form.validateFields();
                if (dataIndex == "ip" && !isIPValid(values[dataIndex], dataIndex)) {
                    message.error(`${title} is invalid.`);
                    return;
                }
                toggleEdit();
                handleSave({ ...record, ...values });
            } catch (errInfo) {
                console.log('Save failed:', errInfo);
            }
        };

        let childNode = children;

        if (editable) {
            childNode = editing ? (
                <Form.Item
                    style={{ margin: 0 }}
                    name={dataIndex}
                    rules={[
                        {
                            required: true,
                            message: `${title} is required.`,
                        },
                    ]}
                >
                    <Input ref={inputRef} onPressEnter={save} onBlur={save} />
                </Form.Item>
            ) : (
                <div
                    className="editable-cell-value-wrap"
                    style={{ paddingRight: 24 }}
                    onClick={toggleEdit}
                >
                    {children}
                </div>
            );
        }

        return <td {...restProps}>{childNode}</td>;
    };

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave: handleSave,
            }),
        };
    });

    return (
<div>
    <TopBreadcrumb />
    <ProCard split="vertical" style={{ marginTop: 20 }}>
        <ProCard title={<div style={{ fontSize: 18, fontWeight: 500}}>IP Whitelist to access AIOps instance</div>} 
                 extra={<Button onClick={handleAdd} icon={<PlusOutlined />} shape="circle" style={{marginRight:"12px"}}/>}
                 headerBordered colSpan="100%">

        <Form form={form} component={false}>
            <Table
                components={{
                    body: {
                        cell: EditableCell,
                    },
                }}
                bordered
                dataSource={dataSource}
                columns={mergedColumns}
                rowClassName="editable-row"
                pagination={false}
                loading={isLoading}
                size="small"
                style={{minHeight:"230px"}}
            />
            <Button
                type="primary"
                style={{
                    marginTop: 16,
                    marginRight: 8,
                }}
                disabled={!isChanged}
                onClick={doSave}
            >
                Save
            </Button>
            <Button
                type="default"
                style={{
                    marginTop: 16,
                }}
                onClick={handleCancel}
            >
                Cancel
            </Button>
        </Form>
        <Modal
                title="Add Public IP"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okButtonProps={{ disabled: isModalOkDisabled }}
            >
                <Form form={modalForm} layout="vertical">
                    <Form.Item
                        name="ip"
                        label="Public IP"
                        rules={[
                            {
                                required: true,
                                message: 'Please input the Public IP',
                            },
                            {
                                validator: (_, value) =>
                                    isIPValid(value, 'ip') ? Promise.resolve() : Promise.reject('IP is invalid.'),
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="note"
                        label="Note"
                        rules={[
                            {
                                required: true,
                                //message: 'Please input the Note',
                            }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>        
	</ProCard>
    </ProCard>
    </div>
    );
};

